import type { HTMLProps } from 'react';
import { useState } from 'react';

import { Typography } from '@remarkable/ark-web';
import type { MutateOptions } from '@tanstack/react-query';

import { Trans } from '@lingui/react/macro';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { usePasscodeReset } from 'src/api/queries';
import { Button, Link } from 'src/components';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

const Layout = ({
  children,
  className = '',
  ...rest
}: HTMLProps<HTMLDivElement>) => (
  <div
    className={
      'my-24 border border-gray-300 p-24 text-left shadow-md ' + className
    }
    {...rest}
  >
    {children}
  </div>
);

export const PasscodeReset = () => {
  const { pendingRequests, approveRequest, denyRequest } = usePasscodeReset();
  const [isError, setIsError] = useState(false);

  // On approve or deny request
  const mutateOptions: MutateOptions<unknown, unknown, string> = {
    onSuccess: () => setIsError(false),
    onError: () => setIsError(true),
  };

  const requestId = pendingRequests.data?.[0]?.RequestID;

  if (approveRequest.isSuccess) {
    return (
      <Layout data-cy="passcode-reset-success-card">
        <Typography variant="body-lg" fontWeight="bold" as="h3">
          <Trans>Passcode has been reset</Trans>
        </Typography>
        <Typography variant="body-md" as="p">
          <Trans>
            Follow the instructions on the device to set a new passcode.
          </Trans>
        </Typography>

        <Button
          className="mt-16"
          variant="secondary"
          size="medium"
          onClick={() => {
            approveRequest.reset();
          }}
          as="button"
        >
          <Trans>Close</Trans>
        </Button>
        <div className="reset-row"></div>
      </Layout>
    );
  }

  if (!requestId) return null;

  if (isError)
    return (
      <Layout data-cy="passcode-reset-error-card">
        <Typography variant="body-lg" fontWeight="bold" as="h3">
          <Trans>Something went wrong</Trans>
        </Typography>
        <Typography variant="body-md" as="p">
          <Trans>
            Please try to reset the passcode from your device again, or{' '}
            <Link
              to={SUPPORT_URL.HOME}
              inline
              trackingLocation={ComponentLocations.FRONT_PAGE.PASSCODE_RESET}
            >
              contact support
            </Link>{' '}
            if the problem persists. We are sorry about the inconvenience.
          </Trans>
        </Typography>
        <Button
          className="mt-16"
          variant="tertiary"
          size="medium"
          onClick={() => {
            approveRequest.isError
              ? approveRequest.mutate(requestId, mutateOptions)
              : denyRequest.mutate(requestId, mutateOptions);
          }}
          loading={approveRequest.isPending || denyRequest.isPending}
          as="button"
        >
          <Trans>Retry</Trans>
        </Button>
      </Layout>
    );

  return (
    <Layout data-cy="passcode-reset-card">
      <Typography variant="body-lg" fontWeight="bold" as="h3">
        <Trans>Reset passcode</Trans>
      </Typography>
      <Typography variant="body-md" as="p">
        <Trans>Are you trying to reset the passcode on your device?</Trans>
      </Typography>
      <div className="mt-16 flex gap-16">
        <Button
          data-cy="deny-passcode-reset-button"
          variant="secondary"
          size="medium"
          onClick={() => denyRequest.mutate(requestId, mutateOptions)}
          loading={denyRequest.isPending}
        >
          <Trans>Deny</Trans>
        </Button>
        <Button
          variant="tertiary"
          data-cy="reset-passcode-button"
          size="medium"
          onClick={() => approveRequest.mutate(requestId, mutateOptions)}
          loading={approveRequest.isPending}
        >
          <Trans>Approve</Trans>
        </Button>
      </div>
    </Layout>
  );
};
